import React, { useEffect, useState } from "react";
import { Sentinel } from "../../model_gen/sentinel";
import { SentinelService } from "../../services/SentinelService";
import {
  Col,
  notification,
  Popover,
  Row,
  Table,
  Tag,
  Typography,
  Empty,
  Select,
} from "antd";
import moment from "moment/moment";
import {
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { usePermissions } from "../../PermissionsProvider";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { Application } from "../../model_gen/application";
import TableDropDownMenu from "../shared/tablemenus/TableDropdownMenu";
import { SentinelListProps, SeverityLevel } from "../../types/ISentinels";
import { Company } from "../../model_gen/company";
import { CompanyService } from "../../services/CompanyService";
import SentinelInfo from "./SentinelInfo";

const { Title } = Typography;

type SentinelResultInterface = {
  sentinel: Sentinel;
  application: Application;
  company: Company;
  total_count: number;
};
const SentinelList: React.FC<SentinelListProps> = ({ emptyText }) => {
  const { apiKey } = usePermissions();

  const [sentinelResults, setSentinelResults] = useState<
    SentinelResultInterface[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);

  const severityColorMap: Record<SeverityLevel, string> = {
    [SeverityLevel.UNRECOGNIZED]: "default",
    [SeverityLevel.INFO]: "default",
    [SeverityLevel.WARN]: "warning",
    [SeverityLevel.ERROR]: "warning",
    [SeverityLevel.FATAL]: "error",
  };

  const severityPopovers: Record<SeverityLevel, string> = {
    [SeverityLevel.UNRECOGNIZED]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.INFO]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.WARN]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.ERROR]: "This Sentinel is NOT evaluating requests.",
    [SeverityLevel.FATAL]: "This Sentinel is evaluating requests.",
  };

  const navigate = useNavigate();

  useEffect(() => {
    CompanyService.getAffiliatedCompanies(null, apiKey).subscribe(
      (companiesData: Company[]) => {
        setCompanies(companiesData);
      },
      (error: any) => {
        console.error(error);
      },
    );
  }, [apiKey]);

  const getSentinels = (offset: number, limit: number) => {
    SentinelService.getAllSentinels(
      offset,
      limit,
      selectedCompanyIds,
      setIsLoading,
      apiKey,
    ).subscribe(
      (sent_results: SentinelResultInterface[]) => {
        setSentinelResults(sent_results);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  const deleteSentinel = (sentinelId: number) => {
    SentinelService.deleteSentinel(sentinelId, setIsLoading, apiKey).subscribe(
      () => {
        // Remove the deleted sentinel from the list
        const updatedSentinels = sentinelResults.filter(
          (sent) => sent.sentinel.id !== sentinelId,
        );
        setSentinelResults(updatedSentinels);
        notification.success({
          message: "Success",
          description: "Sentinel deleted successfully",
        });
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to delete sentinel",
        });
      },
    );
  };

  const sentinelColumns: ColumnsType<SentinelResultInterface> = [
    {
      title: "Company",
      key: "company",
      render: (_, record) => record.company.company_name,
    },
    {
      title: "Application",
      key: "application",
      render: (_, record) => record.application.application_name,
    },
    {
      title: "Name",
      key: "sentinel_name",
      render: (_, record) => record.sentinel.sentinel_name,
    },
    {
      title: "Intents",
      key: "intent",
      render: (_, record) => {
        const tags = record.sentinel.action_types.map((action) => (
          <Tag key={action}>{action}</Tag>
        ));
        return tags;
      },
    },
    {
      title: "State",
      key: "state",
      render: (_, record) =>
        record.sentinel.state === "ACTIVE" ? (
          <Tag color="success">ACTIVE</Tag>
        ) : record.sentinel.state === "DISABLED" ? (
          <Tag color="error">DISABLED</Tag>
        ) : (
          <Tag color="default">DEFAULT</Tag>
        ),
    },
    {
      title: "Severity",
      key: "severity",
      render: (_, record) => {
        const severity = record.sentinel.severity as SeverityLevel;
        return (
          <Popover content={severityPopovers[severity]}>
            <Tag color={severityColorMap[severity]}>
              {SeverityLevel[severity]}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: "Date Created",
      key: "date_created",
      render: (_, record) =>
        moment(record.sentinel.date_created).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, record) => {
        const items = [
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/intent-group/${record.sentinel.intent_group_id}/sentinel/${record.sentinel.id}`,
                  );
                }}>
                Edit Sentinel
              </a>
            ),
            key: "0",
            icon: <EditOutlined />,
          },
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSentinel(record.sentinel.id);
                }}>
                Delete Sentinel
              </a>
            ),
            key: "4",
            icon: <DeleteOutlined />,
          },
        ];
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <TableDropDownMenu items={items} placement="bottomLeft" />
          </div>
        );
      },
      //   render: (_, record) => (
      //     <Button.Group>
      //       <Tooltip title="Edit Sentinel">
      //         <Button
      //           icon={<EditOutlined />}
      //           onClick={(e) => {
      //             e.stopPropagation(); // Prevent row onClick from being triggered
      //             navigate(
      //               `/application/${record.application_id}/sentinel/${record.id}`
      //             );
      //           }}
      //         />
      //       </Tooltip>
      //       <Tooltip title="Clone Sentinel">
      //         <Dropdown
      //           overlay={
      //             <Menu
      //               onClick={(e) => {
      //                 // Handle menu item click, e.key is the applicationId
      //                 console.log(
      //                   `Clone sentinel ${record.id} to application ${e.key}`
      //                 );
      //                 cloneSentinel(record.id, Number(e.key));
      //               }}
      //             >
      //               {applications
      //                 .filter((app) => app.id !== record.application_id)
      //                 .map((app) => (
      //                   <Menu.Item key={app.id}>{app.application_name}</Menu.Item>
      //                 ))}
      //             </Menu>
      //           }
      //           trigger={["click"]}
      //         >
      //           <Button
      //             icon={<CopyOutlined />}
      //             onClick={(e) => e.stopPropagation()}
      //           />
      //         </Dropdown>
      //       </Tooltip>
      //       {record.state === "ACTIVE" || record.state === "ENABLED" ? (
      //         <Tooltip title="Disable Sentinel">
      //           <Button
      //             icon={<StopOutlined />}
      //             onClick={(e) => {
      //               e.stopPropagation(); // Prevent row onClick from being triggered
      //               disableSentinel(record.id);
      //             }}
      //           />
      //         </Tooltip>
      //       ) : (
      //         <Tooltip title="Enable Sentinel">
      //           <Button
      //             icon={<CheckCircleOutlined />}
      //             onClick={(e) => {
      //               e.stopPropagation(); // Prevent row onClick from being triggered
      //               enableSentinel(record.id);
      //             }}
      //           />
      //         </Tooltip>
      //       )}
      //       <Tooltip title="Delete Sentinel">
      //         <Button
      //           icon={<DeleteOutlined />}
      //           onClick={(e) => {
      //             e.stopPropagation(); // Prevent row onClick from being triggered
      //             deleteSentinel(record.id);
      //           }}
      //         />
      //       </Tooltip>
      //     </Button.Group>
      //   ),
    },
  ];

  useEffect(() => {
    const offset = (currentPage - 1) * pageSize;
    getSentinels(offset, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    const offset = (currentPage - 1) * pageSize;
    getSentinels(offset, pageSize);
  }, [selectedCompanyIds, currentPage, pageSize]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  const paginatedSentinels = sentinelResults;

  const defaultEmptyContent = (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
  );

  return (
    <div>
      <Row align="middle" style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Title level={4}>Sentinels</Title>
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <Select
            mode="multiple"
            disabled={isLoading}
            style={{ width: "100%", maxWidth: 400, textAlign: "left" }}
            placeholder="Filter by company"
            onChange={(values: number[]) => {
              setSelectedCompanyIds(values);
              setCurrentPage(1);
            }}
            value={selectedCompanyIds}
            options={companies.map((company) => ({
              label: company.company_name,
              value: company.id,
            }))}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Col>
      </Row>
      <Table
        columns={sentinelColumns}
        dataSource={paginatedSentinels}
        rowKey={(record) => record.sentinel.id}
        expandable={{
          expandedRowRender: (record) => (
            <SentinelInfo sentinel={record.sentinel} />
          ),
        }}
        onRow={(record) => {
          return {
            onClick: (e) => {
              // Check if the click target is not the expand icon
              const target = e.target as HTMLElement;
              if (!target.closest(".ant-table-row-expand-icon")) {
                navigate(
                  `/intent-group/${record.sentinel.intent_group_id}/sentinel/${record.sentinel.id}`,
                );
              }
            },
          };
        }}
        pagination={
          sentinelResults[0]?.total_count > pageSize
            ? {
                current: currentPage,
                pageSize: pageSize,
                total: sentinelResults[0]?.total_count,
                showSizeChanger: false,
              }
            : false
        }
        loading={isLoading}
        onChange={handleTableChange}
        locale={{
          emptyText: emptyText || defaultEmptyContent,
        }}
      />
    </div>
  );
};

export default SentinelList;
